import React, { useEffect } from 'react';
import { ChatAreaWrapper, ChatFloatBtn, ChatPanel } from './styled';
import { MinusSquareFilled, SendOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import { useChatStream } from './hooks/useChatStream';
import { useRetryHandler } from './hooks/useRetryHandler';
import apptracker from '@src/utils/sensor/apptracker';
import { NEXA_QUERY } from '@src/utils/sensor/track';
import {
  NEXA_QUERY_TEMPLATE_CLICK,
  NEXA_ACTION_BUBBLE_EXECUTE,
  notifyLongerRuntime,
  buildInitialQueryParams,
  buildTemplateQueryParams,
  TemplateEventDetail,
  ActionBubbleEventDetail,
} from './utils';
import { useAtomValue } from 'jotai';
import { chatStreamLoadingAtom } from './NexaStore';
import TokenUsage from './elements/TokenUsage';
import useGetTokenUsage from './hooks/useGetTokenUsage';
import { NexaAskRequest } from './types/askNexa';
import useGetDiagnosticModes from './DiagnosticTemplates/hooks/useGetDiagnosticModes';

interface ChatBoxProps {
  initialQuery?: string;
  sessionReady?: boolean;
  askRequest?: NexaAskRequest;
}

const ChatBox: React.FC<ChatBoxProps> = ({ initialQuery, sessionReady = true, askRequest }) => {
  const [text, setText] = React.useState('');
  const isLoading = useAtomValue(chatStreamLoadingAtom);
  const { fetchStream, cancelStream } = useChatStream();

  // Handle retry with entrypoint params for first message
  useRetryHandler({ fetchStream });

  const { isOver } = useGetTokenUsage();
  const { data: diagnosticModes = [] } = useGetDiagnosticModes();

  const maybeNotifyLongerRuntime = React.useCallback((shouldNotify: boolean) => {
    if (shouldNotify) {
      notifyLongerRuntime();
    }
  }, []);

  const handleSearch = () => {
    if (text.trim()) {
      const fetchParams: { query: string; diagnostic?: any } = { query: text };

      fetchStream(fetchParams);
      setText('');
      apptracker(NEXA_QUERY, { value: text });
    }
  };

  // handle template bubble click
  useEffect(() => {
    const handler = (e: CustomEvent<TemplateEventDetail>) => {
      const { fetchParams, shouldNotify } = buildTemplateQueryParams(e.detail);
      maybeNotifyLongerRuntime(shouldNotify);
      fetchStream(fetchParams);
    };
    window.addEventListener(NEXA_QUERY_TEMPLATE_CLICK, handler as EventListener);
    return () => window.removeEventListener(NEXA_QUERY_TEMPLATE_CLICK, handler as EventListener);
  }, [fetchStream, maybeNotifyLongerRuntime]);

  // handle action bubble execute
  useEffect(() => {
    const handler = (e: CustomEvent<ActionBubbleEventDetail>) => {
      fetchStream({ query: e.detail.query });
    };
    window.addEventListener(NEXA_ACTION_BUBBLE_EXECUTE, handler as EventListener);
    return () => window.removeEventListener(NEXA_ACTION_BUBBLE_EXECUTE, handler as EventListener);
  }, [fetchStream]);

  // handle initial query (from Ask Nexa requests)
  // Use a ref to ensure we only trigger once
  const hasTriggeredRef = React.useRef(false);

  useEffect(() => {
    // CRITICAL: Wait for session to be ready before triggering
    // This prevents the race condition where initNexaStore wipes out the query
    if (initialQuery && sessionReady && !hasTriggeredRef.current) {
      // Mark as triggered immediately to prevent any re-runs
      hasTriggeredRef.current = true;

      // Set the text in the textarea temporarily
      setText(initialQuery);

      // Auto-trigger the search after a short delay
      const timer = setTimeout(() => {
        const { fetchParams, shouldNotify } = buildInitialQueryParams(
          initialQuery,
          askRequest,
          diagnosticModes,
        );

        maybeNotifyLongerRuntime(shouldNotify);
        fetchStream(fetchParams);
        apptracker(NEXA_QUERY, { value: initialQuery, source: 'ask-nexa' });

        // Clear the textarea after sending (like handleSearch does)
        setText('');
      }, 100); // Short delay since we're already waiting for sessionReady

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [
    initialQuery,
    sessionReady,
    fetchStream,
    askRequest,
    diagnosticModes,
    maybeNotifyLongerRuntime,
  ]); // sessionReady is critical to prevent race condition

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSearch();
    }
  };

  return (
    <ChatAreaWrapper>
      <TokenUsage />
      <ChatPanel>
        <Input.TextArea
          placeholder="Ask Nexa"
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyPress}
          allowClear={true}
          disabled={isLoading || isOver}
          maxLength={1000}
        />
        {isLoading ? (
          <ChatFloatBtn onClick={cancelStream} icon={<MinusSquareFilled />} />
        ) : (
          <ChatFloatBtn
            icon={<SendOutlined />}
            onClick={handleSearch}
            type={text.length ? 'primary' : 'default'}
            disabled={!text.trim() || isOver}
          />
        )}
      </ChatPanel>
    </ChatAreaWrapper>
  );
};

export default ChatBox;
